import type { DeliveryState, EnvelopeState, RecipientState } from '@signbento/contracts'
import { RELEASE_CONFIG } from './config/release-config.js'
import { err, ok, type Result } from './result.js'
import { instant, MS_PER_HOUR, MS_PER_MINUTE } from './time.js'

/**
 * Delivery observation — PRD §6.4, §6.7, §6.8, §14.3.
 * Delivery state is independent of signing state and never implies submission.
 * Dispatch claims are conditional: queue redelivery does not authorize duplicate
 * sends, and committed state blocks future claims.
 */

export type DeliveryEvent =
  | 'ENQUEUE'
  | 'CLAIM'
  | 'PROVIDER_ACCEPTED'
  | 'PROVIDER_DELIVERED'
  | 'PROVIDER_BOUNCED'
  | 'PROVIDER_COMPLAINED'
  | 'SUPPRESSED'
  | 'PROVIDER_UNKNOWN'
  | 'RECONCILE_ACCEPTED'
  | 'RECONCILE_DELIVERED'
  | 'RECONCILE_BOUNCED'

const DELIVERY_TRANSITIONS = {
  ENQUEUE: { NOT_MANAGED: 'QUEUED' },
  CLAIM: { QUEUED: 'DISPATCHING' },
  PROVIDER_ACCEPTED: { DISPATCHING: 'ACCEPTED' },
  PROVIDER_DELIVERED: { ACCEPTED: 'DELIVERED' },
  PROVIDER_BOUNCED: { ACCEPTED: 'BOUNCED', DISPATCHING: 'BOUNCED' },
  PROVIDER_COMPLAINED: { ACCEPTED: 'COMPLAINED', DELIVERED: 'COMPLAINED' },
  SUPPRESSED: { QUEUED: 'SUPPRESSED', DISPATCHING: 'SUPPRESSED' },
  PROVIDER_UNKNOWN: { DISPATCHING: 'UNKNOWN' },
  RECONCILE_ACCEPTED: { UNKNOWN: 'ACCEPTED' },
  RECONCILE_DELIVERED: { UNKNOWN: 'DELIVERED' },
  RECONCILE_BOUNCED: { UNKNOWN: 'BOUNCED' },
} as const satisfies Record<DeliveryEvent, Partial<Record<DeliveryState, DeliveryState>>>

/** Observation states with no outgoing edges; DELIVERED can still receive a complaint. */
export const TERMINAL_DELIVERY_STATES = [
  'BOUNCED',
  'COMPLAINED',
  'SUPPRESSED',
] as const satisfies readonly DeliveryState[]

export function nextDeliveryState(
  from: DeliveryState,
  event: DeliveryEvent,
): Result<DeliveryState> {
  const table = DELIVERY_TRANSITIONS[event] as Partial<Record<DeliveryState, DeliveryState>>
  const to = table[from]
  if (to !== undefined) return ok(to)
  if (from === 'NOT_MANAGED') return err('OP_DELIVERY_SUPPRESSED')
  return err('OP_DELIVERY_CLAIM_LOST')
}

export type EmailJobKind = 'invitation' | 'reminder' | 'completion' | 'correction'

export interface DispatchClaimInput {
  readonly job_kind: EmailJobKind
  readonly envelope_state: EnvelopeState
  readonly recipient_state: RecipientState
  readonly suppressed: boolean
  /** Job due time; a claim before `due_at` is refused. */
  readonly due_at: string
  readonly now: number
}

/**
 * Conditional claim checks run immediately before dispatch, on committed state —
 * never on the state observed when the job was queued (§6.4).
 */
export function canClaimDispatch(input: DispatchClaimInput): Result<void> {
  if (input.suppressed) return err('OP_DELIVERY_SUPPRESSED')
  if (input.now < instant(input.due_at)) return err('OP_DELIVERY_CLAIM_LOST')

  if (input.job_kind === 'completion') {
    return input.envelope_state === 'COMPLETED' ? ok(undefined) : err('OP_DELIVERY_CLAIM_LOST')
  }
  if (input.envelope_state !== 'ACTIVE') return err('OP_DELIVERY_CLAIM_LOST')

  if (input.job_kind === 'reminder') {
    return input.recipient_state === 'ACTIVE' ? ok(undefined) : err('OP_DELIVERY_CLAIM_LOST')
  }
  // invitation / correction target a recipient who has not yet responded.
  if (input.recipient_state === 'SUBMITTED' || input.recipient_state === 'DECLINED') {
    return err('OP_DELIVERY_CLAIM_LOST')
  }
  return ok(undefined)
}

export interface ReminderBudget {
  readonly reminders_sent: number
  readonly last_sent_at?: string
}

export function withinReminderCaps(budget: ReminderBudget, now: number): Result<void> {
  if (budget.reminders_sent >= RELEASE_CONFIG.reminders_per_recipient.value) {
    return err('OP_DELIVERY_BUDGET_EXCEEDED')
  }
  if (budget.last_sent_at !== undefined) {
    const spacingMs = RELEASE_CONFIG.reminder_min_spacing_hours.value * MS_PER_HOUR
    if (now - instant(budget.last_sent_at) < spacingMs) return err('OP_DELIVERY_BUDGET_EXCEEDED')
  }
  return ok(undefined)
}

/**
 * The "first reminder after" choices — PRD §6.4 ("Off; after 1/2/3/7 days"). Product text, not a
 * register entry; the count cap and the spacing below are register entries and are read from it.
 */
export const REMINDER_FIRST_AFTER_DAY_PRESETS: readonly number[] = [1, 2, 3, 7]

/** A sender's automatic-reminder choice. "Off" is the absence of a policy, never a zero count. */
export interface ReminderPolicyChoice {
  readonly first_after_days: number
  readonly repeat_every_days: number
  readonly max_reminders: number
}

export type ReminderPolicyIssue =
  /** `reminders_per_recipient` or `reminder_min_spacing_hours` is not a usable value: fail closed. */
  'CAPS_UNSET' | 'FIRST_AFTER_NOT_PRESET' | 'REPEAT_BELOW_MIN_SPACING' | 'MAX_OUT_OF_RANGE'

/**
 * Whether a stored or requested reminder policy is acceptable — PRD §6.4, §14.3. `null` means
 * acceptable. The API refuses a create with an issue; the worker treats a stored policy with an
 * issue as no policy at all (reminders off). Every number but the presets comes from the register.
 */
export function validateReminderPolicy(policy: ReminderPolicyChoice): ReminderPolicyIssue | null {
  const cap: number | null = RELEASE_CONFIG.reminders_per_recipient.value
  const spacingHours: number | null = RELEASE_CONFIG.reminder_min_spacing_hours.value
  if (cap === null || !Number.isInteger(cap) || cap <= 0) return 'CAPS_UNSET'
  if (spacingHours === null || !Number.isFinite(spacingHours) || spacingHours <= 0) {
    return 'CAPS_UNSET'
  }
  if (!REMINDER_FIRST_AFTER_DAY_PRESETS.includes(policy.first_after_days)) {
    return 'FIRST_AFTER_NOT_PRESET'
  }
  if (
    !Number.isInteger(policy.repeat_every_days) ||
    policy.repeat_every_days < 1 ||
    policy.repeat_every_days * 24 < spacingHours
  ) {
    return 'REPEAT_BELOW_MIN_SPACING'
  }
  if (
    !Number.isInteger(policy.max_reminders) ||
    policy.max_reminders < 1 ||
    policy.max_reminders > cap
  ) {
    return 'MAX_OUT_OF_RANGE'
  }
  return null
}

export interface ExtraInvitationBudget {
  readonly extra_invitations_sent: number
  readonly last_sent_at?: string
}

export function withinExtraInvitationCaps(
  budget: ExtraInvitationBudget,
  now: number,
): Result<void> {
  if (budget.extra_invitations_sent >= RELEASE_CONFIG.extra_invitations_per_slot.value) {
    return err('OP_DELIVERY_BUDGET_EXCEEDED')
  }
  if (budget.last_sent_at !== undefined) {
    const spacingMs = RELEASE_CONFIG.extra_invitation_min_spacing_minutes.value * MS_PER_MINUTE
    if (now - instant(budget.last_sent_at) < spacingMs) return err('OP_DELIVERY_BUDGET_EXCEEDED')
  }
  return ok(undefined)
}

export interface MessageBudget {
  readonly envelope_count: number
  readonly account_day_count: number
  readonly global_day_count: number
}

/** Budgets cover invitations, reminders, corrections and completion notices. */
export function withinMessageBudgets(budget: MessageBudget): Result<void> {
  if (budget.envelope_count >= RELEASE_CONFIG.emails_per_envelope.value) {
    return err('OP_DELIVERY_BUDGET_EXCEEDED')
  }
  if (budget.account_day_count >= RELEASE_CONFIG.emails_per_account_per_day.value) {
    return err('OP_DELIVERY_BUDGET_EXCEEDED')
  }
  if (budget.global_day_count >= RELEASE_CONFIG.emails_global_per_day.value) {
    return err('OP_DELIVERY_BUDGET_EXCEEDED')
  }
  return ok(undefined)
}
